<template>
  <BaseModal
    :is-open="isOpen"
    title="Kick Player"
    max-width="500px"
    @close="handleCancel"
  >
    <div class="kick-player-container">
      <!-- Warning Icon -->
      <div class="warning-icon">
        <span class="material-symbols-outlined">person_remove</span>
      </div>

      <!-- No Human Players Warning -->
      <div v-if="humanPlayers.length === 0" class="no-players-warning">
        <p class="main-text">No other human players to kick</p>
        <p class="sub-text">All other players are bots or there's only you in the game.</p>
        <button @click="handleCancel" class="btn-close-only">
          <span class="material-symbols-outlined">close</span>
          <span>Close</span>
        </button>
      </div>

      <!-- Player Selection List -->
      <div v-else-if="!selectedPlayer" class="player-selection">
        <p class="selection-prompt">Select a player to kick:</p>
        <div class="player-list">
          <button
            v-for="player in humanPlayers"
            :key="player.id"
            @click="selectPlayer(player)"
            class="player-select-item"
          >
            <div class="player-avatar">
              <img
                v-if="player.profile_picture"
                :src="`/images/profiles/${player.profile_picture}.png`"
                :alt="player.username"
                class="avatar-image"
              />
              <div v-else class="avatar-initials">
                {{ getInitials(player.username || '?') }}
              </div>
            </div>
            <div class="player-details">
              <span class="player-name">{{ player.display_name || player.username }}</span>
              <span class="player-seat">Seat {{ player.seat_position + 1 }} • {{ getTeamLabel(player.seat_position) }}</span>
            </div>
            <span class="material-symbols-outlined select-arrow">chevron_right</span>
          </button>
        </div>
        <button @click="handleCancel" class="btn-cancel-full">
          <span class="material-symbols-outlined">close</span>
          <span>Cancel</span>
        </button>
      </div>

      <!-- Confirm Kick Selected Player -->
      <template v-else>
        <!-- Player Info -->
        <div class="player-info">
          <div class="player-avatar">
            <img
              v-if="selectedPlayer.profile_picture"
              :src="`/images/profiles/${selectedPlayer.profile_picture}.png`"
              :alt="selectedPlayer.username"
              class="avatar-image"
            />
            <div v-else class="avatar-initials">
              {{ getInitials(selectedPlayer.username || '?') }}
            </div>
          </div>

          <div class="player-details">
            <h3 class="player-name">{{ selectedPlayer.display_name || selectedPlayer.username }}</h3>
            <div class="player-seat">
              <span class="material-symbols-outlined">event_seat</span>
              <span>Seat {{ selectedPlayer.seat_position + 1 }}</span>
              <span class="team-badge">{{ getTeamLabel(selectedPlayer.seat_position) }}</span>
            </div>
          </div>
        </div>

        <!-- Confirmation Message -->
        <div class="confirmation-message">
          <p class="main-text">Are you sure you want to kick this player?</p>
          <div class="warning-text">
            <span class="material-symbols-outlined">warning</span>
            <span>
              The player will be immediately removed and replaced with a bot.
            </span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="kick-actions">
          <button @click="clearSelection" class="btn-cancel">
            <span class="material-symbols-outlined">arrow_back</span>
            <span>Back</span>
          </button>
          <button @click="handleConfirm" class="btn-kick">
            <span class="material-symbols-outlined">person_remove</span>
            <span>Kick Player</span>
          </button>
        </div>
      </template>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * KickPlayerModal Component
 *
 * Allows creator to select and kick a human player from the game.
 * Only shows human players (excludes bots and self).
 *
 * Props:
 * - players: Array of all players in the game
 * - current_player_id: ID of the current user (to exclude from list)
 *
 * Emits:
 * - confirm: { player_id: string } - Kick confirmed
 * - cancel: [] - Cancelled
 */

interface Player {
  id: string
  username: string
  display_name?: string
  profile_picture?: string
  seat_position: number
  is_bot: boolean
}

interface Props {
  isOpen: boolean
  data: {
    players?: Player[]
    current_player_id?: string
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  confirm: [{ player_id: string }]
  cancel: []
  close: []
}>()

const selectedPlayer = ref<Player | null>(null)

/**
 * Filter to only human players (excluding self)
 */
const humanPlayers = computed(() => {
  if (!props.data?.players) return []
  return props.data.players.filter(
    (p) => !p.is_bot && p.id !== props.data?.current_player_id
  )
})

/**
 * Reset selection when modal closes
 */
watch(() => props.isOpen, (isOpen) => {
  if (!isOpen) {
    selectedPlayer.value = null
  }
})

/**
 * Get initials from name
 */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Get team label for seat
 */
function getTeamLabel(seat: number): string {
  return seat % 2 === 0 ? 'Team A' : 'Team B'
}

/**
 * Select a player to kick
 */
function selectPlayer(player: Player): void {
  selectedPlayer.value = player
}

/**
 * Clear selection and go back to player list
 */
function clearSelection(): void {
  selectedPlayer.value = null
}

/**
 * Handle confirm kick
 */
function handleConfirm(): void {
  if (selectedPlayer.value) {
    emit('confirm', {
      player_id: selectedPlayer.value.id,
    })
  }
  selectedPlayer.value = null
  emit('close')
}

/**
 * Handle cancel
 */
function handleCancel(): void {
  selectedPlayer.value = null
  emit('cancel')
  emit('close')
}
</script>

<style scoped>
.kick-player-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Warning Icon */
.warning-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  animation: pulse-danger 2s ease-in-out infinite;
}

.warning-icon .material-symbols-outlined {
  font-size: 3rem;
  color: #f87171;
}

@keyframes pulse-danger {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(239, 68, 68, 0);
  }
}

/* Player Info */
.player-info {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.player-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 3px solid rgba(239, 68, 68, 0.3);
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f87171, #ef4444);
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
}

.player-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.player-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.player-seat {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.8);
}

.player-seat .material-symbols-outlined {
  font-size: 1.125rem;
}

.team-badge {
  padding: 0.25rem 0.625rem;
  background: rgba(239, 68, 68, 0.2);
  border-radius: 4px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #f87171;
}

/* Confirmation Message */
.confirmation-message {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: center;
}

.main-text {
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin: 0;
}

.warning-text {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.875rem 1.125rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  font-size: 0.9375rem;
  color: rgba(248, 113, 113, 0.9);
  line-height: 1.5;
  text-align: left;
}

.warning-text .material-symbols-outlined {
  font-size: 1.125rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

/* No Players Warning */
.no-players-warning {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
  padding: 1rem 0;
}

.no-players-warning .sub-text {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
}

.btn-close-only {
  margin-top: 1rem;
  padding: 0.75rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-close-only:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

/* Player Selection */
.player-selection {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}

.selection-prompt {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  text-align: center;
}

.player-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.player-select-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  text-align: left;
}

.player-select-item:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
}

.player-select-item .player-avatar {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}

.player-select-item .player-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.player-select-item .player-name {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.player-select-item .player-seat {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
}

.select-arrow {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.4);
  transition: all 0.2s;
}

.player-select-item:hover .select-arrow {
  color: #f87171;
  transform: translateX(4px);
}

.btn-cancel-full {
  margin-top: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  width: 100%;
}

.btn-cancel-full:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

/* Action Buttons */
.kick-actions {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

.btn-cancel,
.btn-kick {
  flex: 1;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
}

.btn-cancel {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.btn-kick {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-kick:hover {
  background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4);
  transform: translateY(-2px);
}

.btn-kick:active,
.btn-cancel:active {
  transform: translateY(0);
}

.btn-cancel .material-symbols-outlined,
.btn-kick .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Responsive */
@media (max-width: 640px) {
  .warning-icon {
    width: 64px;
    height: 64px;
  }

  .warning-icon .material-symbols-outlined {
    font-size: 2.5rem;
  }

  .player-info {
    flex-direction: column;
    text-align: center;
  }

  .player-details {
    align-items: center;
  }

  .kick-actions {
    flex-direction: column;
  }
}
</style>
